import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { useModalDrag } from "../../hooks/useModalDrag";

interface AddColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string) => void;
  existingColumns: string[];
}

export function AddColumnModal({ isOpen, onClose, onAdd, existingColumns }: AddColumnModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) { setError(t('columns.addColumnModal.nameRequired')); return; }
    if (existingColumns.includes(trimmedName)) { setError(t('columns.addColumnModal.nameExists')); return; }
    onAdd(trimmedName);
    setName("");
    setError(null);
    onClose();
  };

  const handleClose = () => { setName(""); setError(null); onClose(); };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); handleSubmit(); }
  };

  // Window-level ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={handleClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={modalRef}
          className={`bg-white rounded-lg shadow-lg w-full max-w-sm ${position ? '' : 'pointer-events-auto'}`}
          style={position ? { position: 'fixed', left: position.x, top: position.y, pointerEvents: 'auto' } : undefined}
        >
          <div
            className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <h3 className="text-[11px] font-medium text-gray-900">{t('columns.addColumnModal.title')}</h3>
            <button onClick={handleClose} onMouseDown={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="px-3 py-2 space-y-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(null); }}
                onKeyDown={handleKeyDown}
                placeholder="z.B. Backend, Infrastructure, ..."
                className="w-full px-2 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                autoFocus
              />
              {error && <p className="mt-0.5 text-[10px] text-red-500">{error}</p>}
            </div>
            {existingColumns.length > 0 && (
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">
                  {`${t('columns.addColumnModal.existing')} (${existingColumns.length})`}
                </label>
                <div className="flex flex-wrap gap-0.5 max-h-16 overflow-y-auto">
                  {existingColumns.map((col) => (
                    <span key={col} className="px-1 py-px text-[9px] bg-gray-100 text-gray-500 rounded">{col}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="px-3 py-1.5 border-t border-gray-200 flex items-center justify-between">
            <span className="text-[9px] text-gray-400">{`${t('common.escCancel')} · ${t('common.enterSave')}`}</span>
            <div className="flex gap-1.5">
              <button onClick={handleClose} className="px-2 py-1 text-[10px] text-gray-600 hover:bg-gray-100 rounded transition-colors">
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!name.trim()}
                className="px-2 py-1 text-[10px] text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.add')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
